import type {
    ConferenceDay,
    FaqItem,
    GalleryColumn,
    NavLink,
    Session,
    Speaker,
    Stat,
    Theme,
    Ticket,
    TravelOption
} from '@/types'

export const event = {
    name: 'Metric',
    edition: 'Berlin 2026',
    wordmark: 'Metric',
    eyebrow: 'Data · Product · Decisions',
    titleLines: ['Metric', 'Berlin 2026'],
    tagline: 'Three days on how teams measure what matters, and decide what to do about it.',
    dates: 'May 11-13',
    venue: 'Station Berlin',
    facts: [
        { value: '3', label: 'days' },
        { value: '24', label: 'Speakers' },
        { value: '1400', label: 'Attendees' }
    ],
    ticketsUrl: '/contact',
    mapsUrl: 'https://www.google.com/maps/search/?api=1&query=Station+Berlin+Luckenwalder+Strasse+4-6+Berlin',
    contactEmail: 'hello@metric-conf.com'
} as const

export const navLinks: NavLink[] = [
    { label: 'About', href: '/#about' },
    { label: 'Speakers', href: '/#speakers' },
    { label: 'Schedule', href: '/#day-1' },
    { label: 'Location', href: '/#location' },
    { label: 'Travel', href: '/travel' },
    { label: 'Contact', href: '/contact' }
]

export const stats: Stat[] = [
    { value: '24', label: 'voices on stage' },
    { value: '12', label: 'curated sessions' },
    { value: '3', label: 'days in Berlin, Germany' }
]

export const themes: Theme[] = [
    {
        index: '001',
        title: 'Signal & Noise',
        copy: 'Telling a real movement from ordinary variance - and knowing how long to wait before you call it.',
        image: '/images/theme-signal-noise.png'
    },
    {
        index: '002',
        title: 'Decision Systems',
        copy: 'Turning a measurement into a decision somebody can act on, with an owner, a threshold, and a deadline.',
        image: '/images/theme-decision-systems.png'
    },
    {
        index: '003',
        title: 'Beyond the Dashboard',
        copy: 'Measurement as a practice rather than a surface - definitions, instrumentation, and the judgement around both.',
        image: '/images/theme-beyond-dashboard.jpg'
    }
]

export const speakers: Speaker[] = [
    {
        slug: 'frida-nyberg',
        name: 'Frida Nyberg',
        role: 'Chief Data Officer',
        company: 'Zalando',
        image: '/images/speaker-frida-nyberg.png',
        bio: 'Frida runs the data function behind a business selling into twenty-five markets, and argues most of the job is definitions.',
        longBio: [
            'Frida runs the data function behind a business selling into twenty-five markets, where the same word means four different things depending on which team says it.',
            'Her work is mostly definitional: agreeing what a number counts, who owns it, and what happens when it moves. The modelling, she says, is the easy part.',
            'She opens Metric with a keynote about the one number that moved and what it cost to understand it.'
        ],
        links: { linkedin: 'https://www.linkedin.com/', x: 'https://x.com/' }
    },
    {
        slug: 'tomas-ferreira',
        name: 'Tomás Ferreira',
        role: 'Head of Analytics',
        company: 'Spotify',
        image: '/images/speaker-tomas-ferreira.png',
        bio: 'Tomás works on guardrail metrics, and on the expensive lesson of setting them badly.',
        longBio: [
            'Tomás leads analytics for a product where a small ranking change reaches hundreds of millions of people before anyone has read the report.',
            'He works on guardrails: the metrics that are allowed to stop a launch, who gets to set them, and what a team learns the first time one fires incorrectly.',
            'He has spent the last two years arguing that most teams measure too many things and protect too few.'
        ],
        links: { linkedin: 'https://www.linkedin.com/', x: 'https://x.com/' }
    },
    {
        slug: 'nadia-brandt',
        name: 'Nadia Brandt',
        role: 'VP Product',
        company: 'Klarna',
        image: '/images/speaker-nadia-brandt.png',
        bio: 'Nadia builds the path from a dashboard to a decision, and deletes the dashboards nobody decides from.',
        longBio: [
            'Nadia leads product for a payments business where a rounding error is somebody’s rent, and where measurement is a regulated activity as much as a curious one.',
            'Her focus is the short, badly lit path between a dashboard and a decision: who is looking, what they are allowed to change, and how quickly.',
            'She is known internally for deleting dashboards, which she describes as the highest-leverage thing she does.'
        ],
        links: { linkedin: 'https://www.linkedin.com/', x: 'https://x.com/' }
    },
    {
        slug: 'elias-mensah',
        name: 'Elias Mensah',
        role: 'Principal Data Scientist',
        company: 'Duolingo',
        image: '/images/speaker-elias-mensah.png',
        bio: 'Elias teaches teams to read variance before they read the result.',
        longBio: [
            'Elias works on experimentation at a product with a daily habit loop, where almost everything looks significant if you squint at it long enough.',
            'He teaches teams to read variance first and the headline second, and runs the hands-on workshop that has become the most oversubscribed session at every conference he speaks at.',
            'He also writes about the ethics of optimising for engagement when engagement is the point of the product.'
        ],
        links: { linkedin: 'https://www.linkedin.com/', x: 'https://x.com/' }
    },
    {
        slug: 'sofia-marchetti',
        name: 'Sofia Marchetti',
        role: 'Director of Experimentation',
        company: 'Booking.com',
        image: '/images/speaker-sofia-marchetti.png',
        bio: 'Sofia treats sample size as a product decision rather than a statistics one.',
        longBio: [
            'Sofia has run experimentation at one of the highest-volume testing programmes in Europe, where thousands of tests run at once and most of them are wrong.',
            'She argues that sample size is a product decision, not a statistics one: it is a choice about how much you are willing to pay to be sure.',
            'Her sessions are blunt about the tests that should never have been run.'
        ],
        links: { linkedin: 'https://www.linkedin.com/', x: 'https://x.com/' }
    },
    {
        slug: 'jan-kowalczyk',
        name: 'Jan Kowalczyk',
        role: 'Staff Engineer',
        company: 'Vercel',
        image: '/images/speaker-jan-kowalczyk.png',
        bio: 'Jan builds instrumentation, and cares that it is still correct a year later.',
        longBio: [
            'Jan builds the instrumentation layer: the events, the schemas, and the quiet contract that keeps a number meaning the same thing in March as it did in January.',
            'He is interested in the failure nobody notices, where a rename upstream silently changes a metric downstream and the graph stays plausible.',
            'His workshop starts from an empty repository and ends with a product you can actually measure.'
        ],
        links: { linkedin: 'https://www.linkedin.com/', x: 'https://x.com/' }
    },
    {
        slug: 'lina-haddad',
        name: 'Lina Haddad',
        role: 'Head of Growth',
        company: 'Revolut',
        image: '/images/speaker-lina-haddad.png',
        bio: 'Lina runs growth without a single north star, and explains why that was the right call.',
        longBio: [
            'Lina leads growth at a company that added products faster than it could agree on what success looked like for any of them.',
            'She made the unfashionable call to drop the single north-star metric and run a small portfolio instead, and has the two years of results to argue about.',
            'She talks about what a team loses when every decision is squeezed through one number.'
        ],
        links: { linkedin: 'https://www.linkedin.com/', x: 'https://x.com/' }
    },
    {
        slug: 'mattias-lund',
        name: 'Mattias Lund',
        role: 'Founder',
        company: 'Northbound',
        image: '/images/speaker-mattias-lund.png',
        bio: 'Mattias writes about what companies stop measuring, and what that silence says.',
        longBio: [
            'Mattias founded a research studio that works with companies on the metrics they retired, which turns out to be a more revealing list than the ones they kept.',
            'Before that he spent a decade inside consumer businesses watching good measurement drift into theatre.',
            'He closes Metric with a keynote on what we stopped measuring, and what we lost with it.'
        ],
        links: { linkedin: 'https://www.linkedin.com/', x: 'https://x.com/' }
    }
]

export const days: ConferenceDay[] = [
    {
        id: 'day-1',
        label: 'Day 1',
        date: 'Mon, May 11',
        theme: 'Signal & Noise',
        themeCopy: 'Telling a real movement from ordinary variance - and knowing how long to wait before you call it.'
    },
    {
        id: 'day-2',
        label: 'Day 2',
        date: 'Tue, May 12',
        theme: 'Decision Systems',
        themeCopy: 'Turning a measurement into a decision somebody can act on, with an owner, a threshold, and a deadline.'
    },
    {
        id: 'day-3',
        label: 'Day 3',
        date: 'Wed, May 13',
        theme: 'Beyond the Dashboard',
        themeCopy: 'Measurement as a practice rather than a surface - definitions, instrumentation, and the judgement around both.'
    }
]

export const sessions: Session[] = [
    {
        slug: 'the-number-that-moved',
        title: 'The Number That Moved',
        blurb: 'What it cost to understand a single spike.',
        body: [
            'In March a headline number moved eleven percent overnight. It took nine people six days to explain it, and the explanation was not the one anyone expected.',
            'This keynote walks through that week honestly: the wrong theories, the meeting where someone finally asked what the metric actually counted, and the definition change nobody had written down.',
            'It is an argument for treating definitions as infrastructure, and for the unglamorous work of maintaining them.'
        ],
        kind: 'Keynote',
        minutes: 60,
        time: '10:30 AM',
        dayId: 'day-1',
        image: '/images/session-the-number-that-moved.jpg',
        speakers: ['frida-nyberg']
    },
    {
        slug: 'reading-variance',
        title: 'Reading Variance',
        blurb: 'Learning to look at the spread before the headline.',
        body: [
            'Most teams read the result first and the uncertainty second, if at all. That order is why so many launches are celebrated and then quietly reversed.',
            'This hands-on workshop reverses it. We work through real result sets, look at the spread before the effect, and practise saying the sentence that ends most bad decisions: we do not know yet.',
            'Bring a laptop. You will leave able to tell a finding from a fluctuation faster than the person presenting it.'
        ],
        kind: 'Workshop',
        minutes: 90,
        time: '11:45 AM',
        dayId: 'day-1',
        image: '/images/session-reading-variance.jpg',
        speakers: ['elias-mensah']
    },
    {
        slug: 'when-the-metric-lies',
        title: 'When the Metric Lies',
        blurb: 'The number is right and the conclusion is wrong.',
        body: [
            'The uncomfortable cases are not the broken pipelines. They are the metrics that are computed perfectly and still point the team somewhere it should not go.',
            'This panel collects those cases: the proxy that stopped tracking the thing it proxied, the survivorship built into a funnel, the segment that quietly carried the whole result.',
            'Two very different companies, and a lot of disagreement about how much scepticism a team can afford.'
        ],
        kind: 'Panel',
        minutes: 60,
        time: '01:30 PM',
        dayId: 'day-1',
        image: '/images/session-when-the-metric-lies.jpg',
        speakers: ['sofia-marchetti', 'nadia-brandt']
    },
    {
        slug: 'sample-size-product-decision',
        title: 'Sample Size Is a Product Decision',
        blurb: 'How sure you need to be is a choice, not a formula.',
        body: [
            'Power calculations get treated as a statistics problem handed to a statistician. They are actually a product question: how much are you willing to pay, in time and traffic, to be sure?',
            'This talk reframes the decision in those terms, with the arithmetic that lets a product manager argue it properly in a room.',
            'It ends with the tests that should never have been run, and what running them cost.'
        ],
        kind: 'Talk',
        minutes: 60,
        time: '02:45 PM',
        dayId: 'day-1',
        image: '/images/session-sample-size-product-decision.jpg',
        speakers: ['sofia-marchetti']
    },
    {
        slug: 'dashboard-to-decision',
        title: 'From Dashboard to Decision',
        blurb: 'Closing the gap between looking and doing.',
        body: [
            'Most organisations are excellent at looking at numbers and poor at deciding from them. The dashboard is not the bottleneck; the ten metres after it are.',
            'This talk maps that path: who is looking, what they are permitted to change without asking, and how long a decision is allowed to stay open.',
            'It includes a defence of deleting dashboards, which is faster than fixing them and usually more popular afterwards.'
        ],
        kind: 'Talk',
        minutes: 60,
        time: '10:30 AM',
        dayId: 'day-2',
        image: '/images/session-dashboard-to-decision.jpg',
        speakers: ['nadia-brandt']
    },
    {
        slug: 'instrumenting-from-scratch',
        title: 'Instrumenting a Product From Scratch',
        blurb: 'Events, schemas, and the contract underneath them.',
        body: [
            'We start from an empty repository and instrument a small product properly: events, schemas, naming, and the tests that fail when somebody upstream changes a field.',
            'The focus is the year-two problem. Anyone can ship tracking that works this week. The hard part is a number that still means the same thing in twelve months.',
            'Bring a laptop and something you are willing to break.'
        ],
        kind: 'Workshop',
        minutes: 90,
        time: '11:45 AM',
        dayId: 'day-2',
        image: '/images/session-instrumenting-from-scratch.jpg',
        speakers: ['jan-kowalczyk']
    },
    {
        slug: 'who-owns-the-definition',
        title: 'Who Owns the Definition',
        blurb: 'Every company argues about this and few write it down.',
        body: [
            'An active user, a conversion, a churned account. Every company has four versions of each and one of them is in the board deck.',
            'This panel is about ownership: who is allowed to change a definition, who has to be told, and what a company does with the eighteen months of history that used the old one.',
            'Expect specifics, including the governance that worked and the governance that became paperwork.'
        ],
        kind: 'Panel',
        minutes: 60,
        time: '01:30 PM',
        dayId: 'day-2',
        image: '/images/session-who-owns-the-definition.jpg',
        speakers: ['frida-nyberg', 'lina-haddad']
    },
    {
        slug: 'cost-of-a-wrong-guardrail',
        title: 'The Cost of a Wrong Guardrail',
        blurb: 'Set it badly and it blocks the wrong launches.',
        body: [
            'A guardrail metric is a metric with the authority to stop a launch. That authority makes it the highest-stakes number a team defines, and most teams define it in a hurry.',
            'This talk covers what happens when one is set too tight, too loose, or on a proxy: the launches blocked for nothing, and the one that should have been blocked and was not.',
            'It closes with a short list of things worth protecting, and a longer list of things worth ignoring.'
        ],
        kind: 'Talk',
        minutes: 60,
        time: '02:45 PM',
        dayId: 'day-2',
        image: '/images/session-cost-of-a-wrong-guardrail.jpg',
        speakers: ['tomas-ferreira']
    },
    {
        slug: 'growth-without-a-north-star',
        title: 'Growth Without a North Star',
        blurb: 'What a team gains by dropping the single number.',
        body: [
            'The single north-star metric is the most repeated advice in product, and it stopped working for us somewhere around the fourth product line.',
            'This talk is the case for a small portfolio instead: three or four numbers held in tension, with explicit rules for what to do when they disagree.',
            'Two years of results, including the quarter where the portfolio was clearly worse.'
        ],
        kind: 'Talk',
        minutes: 60,
        time: '10:30 AM',
        dayId: 'day-3',
        image: '/images/session-growth-without-a-north-star.jpg',
        speakers: ['lina-haddad']
    },
    {
        slug: 'measuring-what-people-feel',
        title: 'Measuring What People Actually Feel',
        blurb: 'Quantifying the part that resists quantification.',
        body: [
            'Speed, trust, and frustration are the things users describe and the things dashboards struggle with. Most teams settle for a proxy and forget it was one.',
            'This panel compares approaches: instrumented perception, survey work that is not theatre, and the awkward business of putting a number on a feeling.',
            'It includes the measurements that were abandoned, and why.'
        ],
        kind: 'Panel',
        minutes: 60,
        time: '11:45 AM',
        dayId: 'day-3',
        image: '/images/session-measuring-what-people-feel.jpg',
        speakers: ['tomas-ferreira', 'jan-kowalczyk']
    },
    {
        slug: 'ethics-of-optimisation',
        title: 'The Ethics of Optimisation',
        blurb: 'Optimising honestly for something worth optimising.',
        body: [
            'Optimisation is morally neutral until you choose the objective. After that every decision inherits the choice, including the ones nobody revisits.',
            'This talk looks at engagement as an objective, at the metrics that reward extraction, and at the practical question of what a team does when the number goes up and the product gets worse.',
            'No framework. A better set of questions to ask before the objective is set.'
        ],
        kind: 'Talk',
        minutes: 60,
        time: '01:00 PM',
        dayId: 'day-3',
        image: '/images/session-ethics-of-optimisation.jpg',
        speakers: ['elias-mensah', 'mattias-lund']
    },
    {
        slug: 'what-we-stopped-measuring',
        title: 'What We Stopped Measuring',
        blurb: 'The retired metrics say more than the live ones.',
        body: [
            'Ask a company what it measures and you get the strategy it would like to have. Ask what it stopped measuring and you get the one it actually has.',
            'This closing keynote draws on three years of that second question: the metrics quietly retired, the reasons given, and the reasons underneath.',
            'Metric ends here, with the argument the rest of the three days has been circling.'
        ],
        kind: 'Keynote',
        minutes: 90,
        time: '02:30 PM',
        dayId: 'day-3',
        image: '/images/session-what-we-stopped-measuring.jpg',
        speakers: ['mattias-lund']
    }
]

export const partners: string[] = [
    '/images/partner-1.png',
    '/images/partner-2.png',
    '/images/partner-3.png',
    '/images/partner-4.png',
    '/images/partner-5.png',
    '/images/partner-6.png'
]

export const galleryColumns: GalleryColumn[] = [
    {
        label: 'Signal over noise',
        images: ['/images/session-growth-without-a-north-star.jpg', '/images/gallery-01.jpg']
    },
    {
        label: 'Experiment design',
        images: ['/images/session-dashboard-to-decision.jpg', '/images/gallery-02.jpg']
    },
    {
        label: 'Decision velocity',
        images: ['/images/session-reading-variance.jpg', '/images/gallery-03.jpg']
    },
    {
        label: 'Definitions that hold',
        images: ['/images/session-sample-size-product-decision.jpg', '/images/gallery-04.jpg']
    },
    {
        label: 'Beyond the dashboard',
        images: ['/images/theme-beyond-dashboard.jpg', '/images/gallery-05.jpg']
    }
]

export const tickets: Ticket[] = [
    {
        name: 'Early Bird',
        note: 'Save €150 - until Feb 12',
        price: '€349',
        features: [
            '3-day access',
            'Full conference access',
            'All talks and panels',
            'Community events',
            'Coffee & lunch'
        ]
    },
    {
        name: 'Standard Pass',
        note: 'After Feb 12th',
        price: '€499',
        badge: 'Popular',
        features: [
            '3-day access',
            'Full conference access',
            'All talks and panels',
            'Community events',
            'Coffee & lunch'
        ]
    },
    {
        name: 'Workshop Pass',
        note: 'Limited to 60 tickets',
        price: '€899',
        features: [
            '3-day access',
            'Both hands-on workshops',
            'Priority seating in all sessions',
            'Speaker dinner on day two',
            'Coffee, lunch & dinner'
        ]
    }
]

export const travelOptions: TravelOption[] = [
    {
        title: 'By air',
        copy: 'Fly into Berlin Brandenburg (BER), then take the FEX or the S9 into the centre and change at Gleisdreieck.',
        cta: 'Search flights',
        href: 'https://www.google.com/travel/flights'
    },
    {
        title: 'By train',
        copy: 'Long-distance trains arrive at Berlin Hauptbahnhof. From there it is two changes, or fifteen minutes in a taxi.',
        cta: 'Plan route',
        href: 'https://www.bahn.com/en'
    },
    {
        title: 'By U-Bahn',
        copy: 'Take the U1, U2 or U3 to Gleisdreieck. The venue is a four minute walk south along Luckenwalder Straße.',
        cta: 'View directions',
        href: event.mapsUrl
    }
]

export const faqs: FaqItem[] = [
    {
        question: 'What is included in the ticket?',
        answer: 'Every pass covers all three days of talks and panels, the evening community events, and coffee and lunch. The two hands-on workshops are limited by room size and are included only with the Workshop Pass.'
    },
    {
        question: 'Do I need a data background?',
        answer: 'No. Sessions assume you make decisions with numbers, not that you build the pipelines. Product managers, designers, engineers, and founders make up most of the room, and the workshops start from first principles.'
    },
    {
        question: 'Will the sessions be recorded?',
        answer: 'Talks, panels, and keynotes are recorded and shared with every attendee about two weeks after the event. Workshops are not recorded, so people can bring real numbers into the room.'
    },
    {
        question: 'Can I transfer my ticket?',
        answer: 'Yes. Passes can be transferred to another person up to 48 hours before the opening keynote. Email us from the address you booked with and we will reissue it at no cost.'
    },
    {
        question: 'Where is the event located?',
        answer: 'Metric takes place at Station Berlin, Luckenwalder Straße 4-6 in Kreuzberg, a four minute walk from Gleisdreieck on the U1, U2 and U3.'
    }
]

export const footerColumns = [
    {
        title: 'Explore',
        links: [
            { label: 'About', href: '/#about' },
            { label: 'Speakers', href: '/#speakers' },
            { label: 'Schedule', href: '/#day-1' },
            { label: 'Travel', href: '/travel' },
            { label: 'Tickets', href: '/#tickets' },
            { label: 'Contact', href: '/contact' }
        ]
    },
    {
        title: 'Legal',
        links: [
            { label: 'Privacy Policy', href: '/privacy' },
            { label: 'Terms', href: '/terms' },
            { label: 'Cookies', href: '/privacy#cookies' }
        ]
    }
]

export const venueAddress = [
    'Station Berlin',
    'Luckenwalder Straße 4-6',
    'Kreuzberg, Berlin',
    '10963'
]

export const stays = [
    {
        area: 'Kreuzberg',
        walk: '5 min walk',
        copy: 'The neighbourhood the venue sits in. Low buildings, good coffee, and you can leave your room ten minutes before the first keynote.',
        image: '/images/travel-kreuzberg.jpg'
    },
    {
        area: 'Mitte',
        walk: '12 min by U-Bahn',
        copy: 'The centre, and where most of the larger hotels are. Two stops on the U2 and you are at the door, with the museums on your evenings.',
        image: '/images/travel-mitte.jpg'
    },
    {
        area: 'Prenzlauer Berg',
        walk: '20 min by U-Bahn',
        copy: 'Quieter, residential, and full of small restaurants. The right choice if you want the city to feel like somewhere people live.',
        image: '/images/travel-prenzlauer.jpg'
    }
]

export const travelNotes = [
    {
        title: 'Getting around',
        copy: 'One BVG day ticket covers the U-Bahn, S-Bahn, trams and buses in zones AB. Contactless works on the ticket machines, and Gleisdreieck connects three lines.'
    },
    {
        title: 'From the airport',
        copy: 'Berlin Brandenburg is roughly 35 minutes on the FEX to Hauptbahnhof, or 45 on the S9 if you would rather not change. Taxis run about 50 euros.'
    },
    {
        title: 'Weather in May',
        copy: 'Mild and changeable, usually 12-20°C, with a decent chance of rain. Bring a layer for the venue, which is a converted rail depot and runs cold in the mornings.'
    },
    {
        title: 'Visas',
        copy: 'Germany is in the Schengen area, so many nationalities enter without a visa for short stays. Check the rules for your passport early and we will send a letter of invitation if your employer needs one.'
    },
    {
        title: 'Connectivity',
        copy: 'An eSIM is the simplest option and works the moment you land. The venue runs open Wi-Fi across all three halls, and it holds up when everyone joins at once.'
    },
    {
        title: 'Accessibility',
        copy: 'The venue is step free throughout, with lifts to every session room and reserved seating at the front of each hall. Tell us what you need when you book and we will arrange it.'
    }
]

export const getSpeaker = (slug: string): Speaker | undefined => speakers.find((item) => item.slug === slug)

export const getSession = (slug: string): Session | undefined => sessions.find((item) => item.slug === slug)

export const sessionsForDay = (dayId: string): Session[] => sessions.filter((item) => item.dayId === dayId)

export const sessionsForSpeaker = (slug: string): Session[] => sessions.filter((item) => item.speakers.includes(slug))

export const dayById = (id: string): ConferenceDay | undefined => days.find((item) => item.id === id)
